import type { ChangeEvent, JSX } from "react";
import { useState, useId } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Card } from "./ui";
import { SUPPORTED_TAX_YEARS, type TaxYearString } from "../utils/taxYear";

interface CompactTaxYearSelectorProps {
  /**
   * Currently selected tax year
   */
  taxYear: TaxYearString;

  /**
   * Callback when tax year changes
   */
  onTaxYearChange: (taxYear: TaxYearString) => void;

  /**
   * Optional className for styling customization
   */
  className?: string;
}

/**
 * CompactTaxYearSelector - A collapsible tax year selector for settings pages
 *
 * Shows just the tax year text with an expand/collapse icon.
 * When expanded, reveals a dropdown to change the year.
 */
export function CompactTaxYearSelector({
  taxYear,
  onTaxYearChange,
  className,
}: CompactTaxYearSelectorProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const selectId = useId();

  const handleChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    onTaxYearChange(e.target.value as TaxYearString);
  };

  return (
    <Card elevation="md" padding="sm" className={className}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left"
        aria-expanded={isExpanded}
        aria-controls={isExpanded ? selectId : undefined}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Tax Year:</span>
          <span className="text-sm font-semibold text-gray-900">{taxYear}</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-500" aria-hidden="true" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500" aria-hidden="true" />
        )}
      </button>

      {isExpanded && (
        <div id={selectId} className="mt-3">
          <label htmlFor={`${selectId}-select`} className="sr-only">
            Select tax year
          </label>
          <select
            id={`${selectId}-select`}
            value={taxYear}
            onChange={handleChange}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors text-sm"
          >
            {SUPPORTED_TAX_YEARS.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      )}
    </Card>
  );
}
